// Module 2bis — Travail autour des drivers relationnels (cahier d'exercices).
// Fait suite au module IDR-50. Non scoré : réflexion écrite + échelle de difficulté par cas.

export const DIFFICULTY_LEVELS = [
  { value: 0, label: "Très facile" },
  { value: 1, label: "Facile" },
  { value: 2, label: "Moyen" },
  { value: 3, label: "Difficile" },
  { value: 4, label: "Très difficile" },
];

export const EXO_CASES = [
  { key: "utile", label: "Situations où ce driver m'a été utile" },
  { key: "pas_utile", label: "Situations où ce driver ne m'a pas été utile" },
  { key: "aurait_utile", label: "Situations où ce driver m'aurait été utile" },
];

// Traductions ES (contenu affiché dans le parcours entourage) — par clé de driver.
export const DRIVERS_ES = {
  sois_fort: {
    name: "Sé fuerte",
    slogan: "« Aguanto, pase lo que pase »",
    injonction: ["Obligación implícita de no mostrar las emociones", "Valor personal asociado a la resistencia, el control y la autonomía", "Prohibición implícita de pedir ayuda"],
    messages: ["« Debo arreglármelas solo »", "« Mostrar mis emociones es ser débil »", "« No tengo derecho a flaquear »"],
    positifs: ["Capacidad de afrontar la adversidad", "Resistencia psíquica", "Alta autonomía", "Fiabilidad en situaciones de crisis", "Estabilidad emocional aparente"],
    negatifs: ["Dificultad para expresar sus necesidades", "Aislamiento emocional", "Somatización frecuente", "Poca petición de apoyo", "Desgaste psíquico silencioso"],
  },
  sois_parfait: {
    name: "Sé perfecto",
    slogan: "« Si no es perfecto, no es aceptable »",
    injonction: ["Búsqueda constante de excelencia", "Miedo al error y al juicio", "Valor personal condicionado al rendimiento"],
    messages: ["« Puedo hacerlo mejor »", "« El error es inaceptable »", "« Vamos a ver qué está mal »"],
    positifs: ["Trabajo cuidadoso y preciso", "Agudo sentido del detalle", "Alto nivel de exigencia", "Fiabilidad profesional", "Búsqueda de mejora continua"],
    negatifs: ["Insatisfacción crónica", "Procrastinación por miedo a la imperfección", "Autocrítica excesiva", "Rigidez cognitiva", "Dificultad para delegar"],
  },
  fais_efforts: {
    name: "Esfuérzate",
    slogan: "« Si cuesta, es que vale algo »",
    injonction: ["Asociación entre mérito y dificultad", "Desvalorización de la facilidad", "Valoración de la lucha y la perseverancia a toda costa"],
    messages: ["« Hay que sufrir »", "« Si es fácil, es sospechoso »", "« Debo merecer lo que obtengo »"],
    positifs: ["Alta perseverancia", "Tolerancia a la frustración", "Compromiso duradero", "Sentido del esfuerzo", "Capacidad de afrontar tareas complejas"],
    negatifs: ["Sobreinversión inútil", "Dificultad para soltar", "Desvalorización de los éxitos fáciles", "Fatiga crónica", "Poca optimización de los recursos"],
  },
  fais_plaisir: {
    name: "Complace",
    slogan: "« Con tal de que estén contentos »",
    injonction: ["Prioridad a las necesidades de los demás", "Miedo al rechazo o al conflicto", "Valor personal ligado a la aprobación"],
    messages: ["« Debo decir que sí »", "« Si me niego, decepciono »", "« Debo adaptarme »"],
    positifs: ["Sentido relacional desarrollado", "Alta empatía", "Cooperación natural", "Clima relacional apaciguado", "Capacidad de trabajar en equipo"],
    negatifs: ["Dificultad para poner límites", "Frustración acumulada", "Anulación de sí mismo", "Ira contenida", "Falta de asertividad"],
  },
  depeche_toi: {
    name: "Date prisa",
    slogan: "« Más rápido, aún más rápido »",
    injonction: ["Presión constante ligada al tiempo", "Confusión entre rapidez y eficacia", "Intolerancia a la espera y a la lentitud"],
    messages: ["« No tengo tiempo »", "« Rápido, rápido »", "« Tiene que ir más rápido »"],
    positifs: ["Alta reactividad", "Dinamismo", "Capacidad de gestionar la urgencia", "Adaptabilidad", "Puesta en acción rápida"],
    negatifs: ["Estrés permanente", "Impulsividad", "Errores por descuido", "Dificultad para priorizar", "Fatiga nerviosa"],
  },
};

export const EXO_DRIVERS = [
  {
    key: "sois_fort",
    name: "Sois fort",
    antidote: "Savoir extérioriser",
    color: "#8A6D56",
    injonction: [
      "Obligation implicite de ne pas montrer ses émotions",
      "Valeur personnelle associée à la résistance, au contrôle et à l'autonomie",
      "Interdiction implicite de demander de l'aide",
    ],
    slogan: "« Je tiens, quoi qu'il arrive »",
    messages: ["« Je dois gérer seul »", "« Montrer mes émotions, c'est être faible »", "« Je n'ai pas le droit de flancher »"],
    positifs: [
      "Capacité à faire face à l'adversité",
      "Endurance psychique",
      "Autonomie élevée",
      "Fiabilité dans les situations de crise",
      "Stabilité émotionnelle apparente",
    ],
    negatifs: [
      "Difficulté à exprimer ses besoins",
      "Isolement émotionnel",
      "Somatisation fréquente",
      "Faible demande de soutien",
      "Usure psychique silencieuse",
    ],
    risques: ["Burn-out", "Troubles psychosomatiques", "Difficultés relationnelles", "Déconnexion émotionnelle", "Effondrement tardif et brutal"],
    rupture: [
      "Quand le contrôle émotionnel dépasse les capacités de régulation",
      "Quand le corps exprime ce que l'émotion ne peut plus dire",
      "Quand l'entourage ne perçoit plus aucun signal de demande d'aide",
    ],
    coupure: ["De ses émotions authentiques", "Du soutien relationnel", "De l'empathie envers soi-même", "De la capacité à demander", "De l'écoute des limites corporelles"],
  },
  {
    key: "sois_parfait",
    name: "Sois parfait",
    antidote: "Savoir (se) valoriser",
    color: "#A3826A",
    injonction: ["Recherche constante d'excellence", "Peur de l'erreur et du jugement", "Valeur personnelle conditionnée à la performance"],
    slogan: "« Si ce n'est pas parfait, ce n'est pas acceptable »",
    messages: ["« Je peux mieux faire »", "« L'erreur est inacceptable »", "« On va voir ce qui ne va pas »"],
    positifs: ["Travail soigné et précis", "Sens aigu du détail", "Haut niveau d'exigence", "Fiabilité professionnelle", "Recherche d'amélioration continue"],
    negatifs: ["Insatisfaction chronique", "Procrastination par peur de l'imperfection", "Auto-critique excessive", "Rigidité cognitive", "Difficulté à déléguer"],
    risques: ["Anxiété de performance", "Épuisement mental", "Blocages décisionnels", "Perte de plaisir", "Relations tendues"],
    rupture: ["Quand l'exigence devient paralysante", "Quand l'erreur est vécue comme une menace identitaire", "Quand le contrôle remplace totalement le plaisir"],
    coupure: ["De la spontanéité", "De la créativité", "De l'apprentissage par l'erreur", "Du plaisir de faire", "Du sentiment de satisfaction"],
  },
  {
    key: "fais_efforts",
    name: "Fais des efforts",
    antidote: "Savoir équilibrer",
    color: "#B0553B",
    injonction: ["Association entre mérite et difficulté", "Dévalorisation de la facilité", "Valorisation de la lutte et de la persévérance coûte que coûte"],
    slogan: "« Si c'est dur, c'est que ça vaut quelque chose »",
    messages: ["« Il faut en baver »", "« Si c'est facile, c'est suspect »", "« Je dois mériter ce que j'obtiens »"],
    positifs: ["Persévérance élevée", "Tolérance à la frustration", "Engagement durable", "Sens de l'effort", "Capacité à affronter des tâches complexes"],
    negatifs: ["Surinvestissement inutile", "Difficulté à lâcher prise", "Dévalorisation des réussites faciles", "Fatigue chronique", "Faible optimisation des ressources"],
    risques: ["Épuisement progressif", "Auto-sabotage", "Perte d'efficacité", "Déséquilibre vie personnelle / professionnelle", "Vision douloureuse de la réussite"],
    rupture: ["Quand la fatigue devient chronique", "Quand la difficulté est recherchée pour elle-même", "Quand la facilité est vécue comme illégitime ou suspecte"],
    coupure: ["De la fluidité", "Du plaisir dans l'action", "De l'efficacité réelle", "Du repos réparateur", "De la reconnaissance de ses compétences"],
  },
  {
    key: "fais_plaisir",
    name: "Fais plaisir",
    antidote: "Savoir dire non",
    color: "#B58C67",
    injonction: ["Priorité donnée aux besoins des autres", "Peur du rejet ou du conflit", "Valeur personnelle liée à l'approbation"],
    slogan: "« Pourvu qu'ils soient contents »",
    messages: ["« Je dois dire oui »", "« Si je refuse, je déçois »", "« Je dois m'adapter »"],
    positifs: ["Sens relationnel développé", "Empathie élevée", "Coopération naturelle", "Climat relationnel apaisé", "Capacité à travailler en équipe"],
    negatifs: ["Difficulté à poser des limites", "Frustration accumulée", "Effacement de soi", "Colère rentrée", "Manque d'assertivité"],
    risques: ["Épuisement émotionnel", "Relations déséquilibrées", "Perte d'identité personnelle", "Ressentiment", "Dépendance affective"],
    rupture: ["Quand les limites personnelles disparaissent", "Quand la colère devient silencieuse ou explosive", "Quand le lien repose uniquement sur l'adaptation"],
    coupure: ["De ses besoins propres", "De son identité personnelle", "De l'authenticité relationnelle", "De l'assertivité", "Du respect de soi"],
  },
  {
    key: "depeche_toi",
    name: "Dépêche-toi",
    antidote: "Savoir prioriser",
    color: "#C08552",
    injonction: ["Pression constante liée au temps", "Confusion entre rapidité et efficacité", "Intolérance à l'attente et au ralentissement"],
    slogan: "« Plus vite, encore plus vite »",
    messages: ["« Je n'ai pas le temps »", "« Vite, vite »", "« Il faut que ça aille plus vite »"],
    positifs: ["Réactivité élevée", "Dynamisme", "Capacité à gérer l'urgence", "Adaptabilité", "Mise en action rapide"],
    negatifs: ["Stress permanent", "Impulsivité", "Erreurs d'inattention", "Difficulté à prioriser", "Fatigue nerveuse"],
    risques: ["Anxiété chronique", "Décisions précipitées", "Erreurs répétées", "Tensions relationnelles", "Épuisement"],
    rupture: ["Quand le stress devient permanent", "Quand la précipitation génère des erreurs coûteuses", "Quand le corps n'arrive plus à ralentir"],
    coupure: ["De la présence à soi", "De la qualité de l'attention", "De la réflexion approfondie", "De l'écoute de l'autre", "Du plaisir du moment"],
  },
];
